<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed } from 'vue';

import GenericModal from '~/components/shared/GenericModal.vue';
import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import { formatTargetRejectionReason } from '../utils';

type ModalMode = 'all-rejected' | 'partial';

const props = defineProps<{
  acceptedCount: number;
  mode: ModalMode;
  rejected: CampaignManagementApi.CreateCampaignRejectedTarget[];
  show: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const isOpen = computed({
  get: () => props.show,
  set: (value: boolean) => {
    if (!value) {
      emit('close');
    }
  },
});

const rejectedCount = computed(() => props.rejected.length);

const title = computed(() => (
  props.mode === 'partial'
    ? 'Campanha criada com ressalvas'
    : 'Nenhum parceiro incluído'
));

const summary = computed(() => {
  if (props.mode === 'all-rejected') {
    return (
      'A campanha não foi criada. Todos os parceiros informados já participam de '
      + 'outra campanha ativa ou agendada no período.'
    );
  }

  if (props.acceptedCount === 0) {
    return (
      `${rejectedCount.value} parceiro(s) não puderam ser incluídos porque já participam de `
      + 'outra campanha ativa ou agendada no período:'
    );
  }

  return (
    `A campanha foi criada com ${props.acceptedCount} parceiro(s). `
    + `${rejectedCount.value} não puderam ser incluídos porque já participam de `
    + 'outra campanha ativa ou agendada no período:'
  );
});

function formatReason(item: CampaignManagementApi.CreateCampaignRejectedTarget): string {
  return formatTargetRejectionReason(item.errors);
}

function handleClose() {
  emit('close');
}
</script>

<template>
  <GenericModal
    v-model="isOpen"
    :title="title"
    primary-button-text="Entendi"
    :show-secondary-button="false"
    @primary-action="handleClose"
  >
    <p class="modal-summary">{{ summary }}</p>

    <div v-if="mode === 'partial' && rejected.length > 0" class="rejected-panel">
      <div class="rejected-panel-header">
        {{ rejectedCount }} parceiro(s) rejeitado(s)
      </div>
      <ul class="rejected-list">
        <li
          v-for="item in rejected"
          :key="item.target_id"
          class="rejected-item"
        >
          <span class="rejected-id">ID {{ item.target_id }}</span>
          <span class="rejected-reason">{{ formatReason(item) }}</span>
        </li>
      </ul>
    </div>

    <p v-if="mode === 'all-rejected'" class="modal-hint">
      Remova ou substitua os parceiros e tente novamente. O restante do formulário foi mantido.
    </p>
  </GenericModal>
</template>

<style scoped lang="scss">
.modal-summary {
  margin: 0 0 16px;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.modal-hint {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.rejected-panel {
  border: 1px solid #e4e6eb;
  border-radius: 10px;
  overflow: hidden;
}

.rejected-panel-header {
  padding: 10px 14px;
  background: #f5f6f8;
  border-bottom: 1px solid #e4e6eb;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.rejected-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(320px, 45vh);
  overflow-y: auto;
}

.rejected-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border-bottom: 1px solid #e4e6eb;

  &:last-child {
    border-bottom: none;
  }
}

.rejected-id {
  font-size: 13px;
  font-weight: 600;
  color: #111;
}

.rejected-reason {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}
</style>
